import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Group,
  NativeSelect,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { api, errorMessage } from '../api/client';
import type { BrandSummary, EmailList } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { ReadEdit } from '../components/ReadEdit';

/**
 * The lists one masthead offers, told apart from each other.
 *
 * A list belongs to a brand, not to the publisher, because a daily briefing and a sport newsletter
 * on the same masthead are different promises to a reader even though both live under one
 * publisher's roof. Every brand starts with one list, marked Default, which is what a signup means
 * when the interaction offering it does not say otherwise.
 *
 * There is no delete here, deliberately. A list holds the consent records that are a publisher's
 * evidence for having emailed somebody, so destroying it destroys their defence. Archiving is the
 * whole lifecycle a list has after it is made: it hides the list from the signup picker and stops
 * new members joining, while everybody already on it stays readable and exportable. The default
 * list cannot be archived at all, which is why it is never offered the button rather than offered
 * one that would fail.
 */
export function Lists() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const orgName = org?.name ?? 'this publisher';
  const isOwner = org?.role === 'owner';

  const [brands, setBrands] = useState<readonly BrandSummary[] | null>(null);
  const [brandId, setBrandId] = useState('');
  const [brandsProblem, setBrandsProblem] = useState<string | null>(null);

  const [lists, setLists] = useState<readonly EmailList[] | null>(null);
  const [listsProblem, setListsProblem] = useState<string | null>(null);

  useEffect(() => {
    if (orgId === null) return;
    setBrandsProblem(null);
    api
      .brands(orgId)
      .then((body) => {
        setBrands(body.brands);
        const first = body.brands[0];
        setBrandId((current) => (current === '' && first !== undefined ? first.brand_id : current));
      })
      .catch((error: unknown) => setBrandsProblem(errorMessage(error)));
  }, [orgId]);

  const load = useCallback(() => {
    if (orgId === null || brandId === '') return;
    setListsProblem(null);
    setLists(null);
    api
      .lists(orgId, brandId)
      .then((body) => setLists(body.lists))
      .catch((error: unknown) => setListsProblem(errorMessage(error)));
  }, [orgId, brandId]);

  useEffect(load, [load]);

  function replaceList(next: EmailList) {
    setLists((current) =>
      current === null
        ? current
        : current.map((list) => (list.list_id === next.list_id ? next : list)),
    );
  }

  const brandName = brands?.find((brand) => brand.brand_id === brandId)?.name ?? 'this brand';
  const active = (lists ?? []).filter((list) => !list.archived);
  const archived = (lists ?? []).filter((list) => list.archived);

  return (
    <>
      <Title order={1} size="h2">
        Lists
      </Title>
      <Text size="lg" mt="sm" mb="lg">
        A masthead with a daily briefing, a sport newsletter and a weekend read can tell them apart
        here, so a reader who signs up from a sport article and again from a politics article ends
        up on two lists rather than one undifferentiated subscription. Nothing here sends anything:
        quizee collects the signup and hands it on to whatever {brandName} already sends with.
      </Text>

      {brandsProblem !== null && <ErrorNote message={brandsProblem} />}

      {brands !== null && (
        <NativeSelect
          id="lists-brand"
          label="Brand"
          value={brandId}
          onChange={(event) => setBrandId(event.target.value)}
          data={brands.map((brand) => ({ value: brand.brand_id, label: brand.name }))}
          disabled={brands.length === 0}
          style={{ maxWidth: '20rem' }}
          mb="lg"
        />
      )}

      {brands !== null && brands.length === 0 && (
        <Text c="dimmed" mt="sm">
          There are no brands yet, so there is nothing to make a list on.
        </Text>
      )}

      {!isOwner && brandId !== '' && (
        <Text c="dimmed" size="sm" mt="sm">
          Only an owner can make, rename or archive a list, because a list is what a signup's
          consent record points at. Ask one of the owners of {orgName} if you need a new one.
        </Text>
      )}

      {listsProblem !== null && <ErrorNote message={listsProblem} onRetry={load} />}
      {listsProblem === null && lists === null && brandId !== '' && (
        <Loading label={`Loading ${brandName}'s lists`} />
      )}

      {lists !== null && (
        <Stack component="ul" gap="sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {active.map((list) => (
            <ListCard
              key={list.list_id}
              orgId={orgId ?? ''}
              brandId={brandId}
              list={list}
              canEdit={isOwner}
              onSaved={replaceList}
              onArchived={replaceList}
            />
          ))}
        </Stack>
      )}

      {lists !== null && active.length === 0 && (
        <Text c="dimmed" mt="sm">
          No current lists on {brandName}.
        </Text>
      )}

      {archived.length > 0 && (
        <>
          <Title order={2} size="h3" mt="xl">
            Archived
          </Title>
          <Text size="sm" c="dimmed" mt={4}>
            Hidden from the signup picker and closed to new members. Everybody already on one of
            these stays readable and exportable, on the subscribers screen.
          </Text>
          <Stack
            component="ul"
            gap="xs"
            mt="sm"
            style={{ listStyle: 'none', margin: 0, padding: 0 }}
          >
            {archived.map((list) => (
              <Card component="li" key={list.list_id} withBorder radius="md" p="md">
                <Group gap="xs" align="center">
                  <Text fw={500}>{list.name}</Text>
                  <Badge size="sm" radius="sm" variant="light" color="gray" tt="none">
                    Archived
                  </Badge>
                </Group>
                {list.description !== null && list.description !== '' && (
                  <Text size="sm" c="dimmed" mt={2}>
                    {list.description}
                  </Text>
                )}
              </Card>
            ))}
          </Stack>
        </>
      )}

      {isOwner && brandId !== '' && (
        <CreateListForm
          orgId={orgId ?? ''}
          brandId={brandId}
          brandName={brandName}
          onCreated={(created) => {
            setLists((current) => (current === null ? [created] : [...current, created]));
          }}
        />
      )}
    </>
  );
}

/** What the rename form holds while it is open. */
interface ListDraft {
  readonly name: string;
  readonly description: string;
}

function ListCard({
  orgId,
  brandId,
  list,
  canEdit,
  onSaved,
  onArchived,
}: {
  orgId: string;
  brandId: string;
  list: EmailList;
  canEdit: boolean;
  onSaved: (list: EmailList) => void;
  onArchived: (list: EmailList) => void;
}) {
  const [problem, setProblem] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveProblem, setArchiveProblem] = useState<string | null>(null);

  async function save(draft: ListDraft): Promise<boolean> {
    setProblem(null);
    try {
      const body = await api.updateList(orgId, brandId, list.list_id, {
        name: draft.name.trim(),
        description: draft.description.trim(),
      });
      onSaved(body.list);
      notifications.show({ message: 'List saved.', color: 'violet' });
      return true;
    } catch (error) {
      setProblem(errorMessage(error));
      return false;
    }
  }

  async function archive() {
    setArchiveProblem(null);
    setArchiving(true);
    try {
      const body = await api.archiveList(orgId, brandId, list.list_id);
      onArchived(body.list);
      notifications.show({ message: `${list.name} archived.`, color: 'violet' });
    } catch (error) {
      setArchiveProblem(errorMessage(error));
    } finally {
      setArchiving(false);
      setConfirming(false);
    }
  }

  return (
    <Card component="li" withBorder radius="md" p="md">
      {problem !== null && <ErrorNote message={problem} />}
      <ReadEdit<ListDraft>
        noun={`the list "${list.name}"`}
        canEdit={canEdit}
        open={() => ({ name: list.name, description: list.description ?? '' })}
        check={(draft) => (draft.name.trim() === '' ? ['Give the list a name.'] : [])}
        save={save}
        openLabel="Rename"
        openName={`Rename ${list.name}`}
        saveLabel="Save list"
        saveName={`Save the list ${list.name}`}
        form={(draft, set) => (
          <Stack gap="md">
            <TextInput
              id={`list-name-${list.list_id}`}
              label="Name"
              value={draft.name}
              maxLength={200}
              onChange={(event) => set({ ...draft, name: event.currentTarget.value })}
              style={{ maxWidth: '24rem' }}
            />
            <Textarea
              id={`list-description-${list.list_id}`}
              label="Description"
              description="For your own team. Readers never see this."
              autosize
              minRows={1}
              maxRows={4}
              maxLength={500}
              value={draft.description}
              onChange={(event) => set({ ...draft, description: event.currentTarget.value })}
              style={{ maxWidth: '28rem' }}
            />
          </Stack>
        )}
      >
        <Group gap="xs" align="center" wrap="wrap">
          <Text fw={500}>{list.name}</Text>
          {list.is_default && (
            <Badge size="sm" radius="sm" variant="light" color="violet" tt="none">
              Default
            </Badge>
          )}
        </Group>
        <Text size="sm" c="dimmed" mt={2}>
          {list.description !== null && list.description !== ''
            ? list.description
            : 'No description.'}
        </Text>
      </ReadEdit>

      {canEdit && !list.is_default && !confirming && (
        <Button
          variant="subtle"
          color="red"
          size="compact-sm"
          px={0}
          mt="sm"
          onClick={() => {
            setArchiveProblem(null);
            setConfirming(true);
          }}
          aria-label={`Archive ${list.name}`}
        >
          Archive this list
        </Button>
      )}

      {archiveProblem !== null && <ErrorNote message={archiveProblem} />}

      {confirming && (
        <Card withBorder radius="md" p="md" mt="sm">
          <Text size="sm" fw={500}>
            Archive {list.name}?
          </Text>
          <Text size="sm" mt={4}>
            This is not a delete. {list.name} disappears from the signup picker so nothing new can
            join it, but everybody already on it stays exactly where they are: readable on the
            subscribers screen and included in a CSV export. There is no way to bring it back to the
            picker afterwards.
          </Text>
          <Group mt="sm">
            <Button
              color="red"
              size="compact-sm"
              disabled={archiving}
              onClick={() => void archive()}
              autoFocus
            >
              {archiving ? 'Archiving...' : `Yes, archive ${list.name}`}
            </Button>
            <Button
              variant="default"
              size="compact-sm"
              disabled={archiving}
              onClick={() => setConfirming(false)}
            >
              Keep it
            </Button>
          </Group>
        </Card>
      )}
    </Card>
  );
}

function CreateListForm({
  orgId,
  brandId,
  brandName,
  onCreated,
}: {
  orgId: string;
  brandId: string;
  brandName: string;
  onCreated: (list: EmailList) => void;
}) {
  const [problem, setProblem] = useState<string | null>(null);

  async function create(draft: ListDraft): Promise<boolean> {
    setProblem(null);
    try {
      const body = await api.createList(orgId, brandId, {
        name: draft.name.trim(),
        description: draft.description.trim(),
      });
      onCreated(body.list);
      notifications.show({ message: 'List created.', color: 'violet' });
      return true;
    } catch (error) {
      setProblem(errorMessage(error));
      return false;
    }
  }

  return (
    <Card component="section" withBorder radius="lg" p="lg" mt="xl">
      <Title order={2} size="h3">
        Make a list
      </Title>
      <Text size="sm" c="dimmed" mt={4}>
        Give {brandName} another list to point a signup at, such as a sport briefing kept apart from
        the daily one.
      </Text>
      {problem !== null && <ErrorNote message={problem} />}

      <ReadEdit<ListDraft>
        noun="the new list"
        canEdit
        asForm
        open={() => ({ name: '', description: '' })}
        openLabel="Make a list"
        openName="Make a list"
        saveLabel="Create list"
        saveName="Create list"
        busyLabel="Creating..."
        problemsTitle="Before a list can be made:"
        check={(draft) => (draft.name.trim() === '' ? ['Give the list a name.'] : [])}
        save={create}
        form={(draft, set) => (
          <Stack gap="md" mt="md">
            <TextInput
              id="new-list-name"
              label="Name"
              description="What editors will see when they pick a list for a signup, such as Sport briefing."
              value={draft.name}
              maxLength={200}
              onChange={(event) => set({ ...draft, name: event.currentTarget.value })}
              style={{ maxWidth: '24rem' }}
            />
            <Textarea
              id="new-list-description"
              label="Description"
              description="Optional, and for your own team. Readers never see this."
              autosize
              minRows={1}
              maxRows={4}
              maxLength={500}
              value={draft.description}
              onChange={(event) => set({ ...draft, description: event.currentTarget.value })}
              style={{ maxWidth: '28rem' }}
            />
          </Stack>
        )}
      />
    </Card>
  );
}
